// Drawer UX preferences stored in localStorage (not NgRx — no cross-cutting state).
const STORAGE_KEY = 'helios.commentary.drawer.preferences/v1'

export interface CommentsDrawerPreferences {
  /** When true, the drawer is rendered as a free-floating, draggable modal. */
  floating: boolean
  /** Width when docked, in pixels. Always honored. */
  dockedWidth: number
  /** Width when floating, in pixels. */
  floatingWidth: number
  /** Height when floating, in pixels. */
  floatingHeight: number
  /** Top-left x of the floating window, in local (pre-zoom) pixels. */
  floatingX: number
  /** Top-left y of the floating window, in local (pre-zoom) pixels. */
  floatingY: number
}

export const DEFAULT_PREFERENCES: CommentsDrawerPreferences = {
  floating: false,
  dockedWidth: 380,
  floatingWidth: 460,
  floatingHeight: 640,
  floatingX: 120,
  floatingY: 80,
}

const BOUNDS = {
  dockedWidth: { min: 280, max: 900 },
  floatingWidth: { min: 320, max: 1200 },
  floatingHeight: { min: 240, max: 1400 },
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, n))
}

export function clampPreferences(
  p: CommentsDrawerPreferences,
): CommentsDrawerPreferences {
  return {
    floating: !!p.floating,
    dockedWidth: clamp(
      p.dockedWidth,
      BOUNDS.dockedWidth.min,
      BOUNDS.dockedWidth.max,
    ),
    floatingWidth: clamp(
      p.floatingWidth,
      BOUNDS.floatingWidth.min,
      BOUNDS.floatingWidth.max,
    ),
    floatingHeight: clamp(
      p.floatingHeight,
      BOUNDS.floatingHeight.min,
      BOUNDS.floatingHeight.max,
    ),
    // x/y are clamped later against the live viewport — no bounds here.
    floatingX: Number.isFinite(p.floatingX) ? p.floatingX : 0,
    floatingY: Number.isFinite(p.floatingY) ? p.floatingY : 0,
  }
}

/** `viewport` is in the same local (pre-zoom) space as the prefs it bounds. */
export function clampToViewport(
  p: CommentsDrawerPreferences,
  viewport: { width: number; height: number },
): CommentsDrawerPreferences {
  const maxX = Math.max(0, viewport.width - p.floatingWidth)
  const maxY = Math.max(0, viewport.height - p.floatingHeight)
  return {
    ...p,
    floatingX: clamp(p.floatingX, 0, maxX),
    floatingY: clamp(p.floatingY, 0, maxY),
  }
}

export function loadPreferences(
  storage: Pick<Storage, 'getItem'> = localStorage,
): CommentsDrawerPreferences {
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_PREFERENCES }
    const parsed = JSON.parse(raw) as Partial<CommentsDrawerPreferences>
    return clampPreferences({ ...DEFAULT_PREFERENCES, ...parsed })
  } catch {
    return { ...DEFAULT_PREFERENCES }
  }
}

export function savePreferences(
  prefs: CommentsDrawerPreferences,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(clampPreferences(prefs)))
  } catch {
    // localStorage may be unavailable (e.g. Safari private mode)
  }
}
